/**
 * Tools to manipulate data arrays
 * @packageDocumentation
 */

import isEmpty from "lodash.isempty";
import isEqual from "lodash.isequal";
import xorWith from "lodash.xorwith";
import { anyReallyAny } from "./object.tools";

/**
 * @returns true if array is empty or null or undefined
 */
export function isEmptyOrNotDefined(ar: unknown[] | null | undefined): boolean {
    if (ar == null) { return true; }
    return ar.length === 0;
}

/**
 * @returns true if array is NOT (empty or null or undefined)
 */
export function isDefinedAndNotEmpty<T>(ar: T[] | null | undefined): ar is T[] {
    if (ar == null) { return false; }
    return ar.length > 0;
}

/**
 * Returns the first key that is lower or equal to the given number key, or null if no key matches (key < lowest key value).
 * __sortedKeys must be sorted...__
 * @param sortedKeys The keys array already sorted
 * @param key The number we compare keys with
 * @returns The first key or null
 */
export function floorKey(sortedKeys: number[], key: number): number | null {
    if (!Array.isArray(sortedKeys) || sortedKeys.length === 0) { return null; }

    let i = binarySearch(sortedKeys, key);
    if (i >= 0) {
        return sortedKeys[i];
    }
    i = ~i;// /if the return value was negative, the bitwise complement of the return value is the correct index for this object
    if (i > 0) {
        return sortedKeys[i - 1];
    }
    return null;// before first time in array
}

/**
 * Searches the index of the target element in the array. Array must be sorted according to the comparator.
 * If not found, the value return is negative, bitwise complement of the index of the first element superior to the target _(i.e. possible insertion index)_
 * @typeParam T The array and target type
 * @param arr The array that we explore
 * @param target The element we search the index in the array
 * @param comparator Comparator callback to sort array
 * @returns The index target in the array or negative number
 */
export function binarySearch<T>(arr: T[], target: T, comparator?: (a: T, b: T) => number): number {
    let l = 0;
    let h = arr.length - 1;
    let m: number, comparison: number;
    if (!comparator) { comparator = (a: anyReallyAny, b: anyReallyAny): number => { return a - b; }; }// using anyReallyAny because we can really compare any kind of value
    while (l <= h) {
        m = l + h >>> 1; /* equivalent to Math.floor((l + h) / 2) but faster */
        comparison = comparator(arr[m], target);
        if (comparison < 0) {
            l = m + 1;
        } else if (comparison > 0) {
            h = m - 1;
        } else {
            return m;
        }
    }
    return ~l;// equivalent to -l -1
}

/**
 * Inserts an element at the right place in the sorted Array and returns the insertion index.
 * @template T The array and target type
 * @param arr The sorted array 
 * @param target The element we insert in the sorted array
 * @param allowDuplicate (allowDuplicate = true) target will be inserted even if it is already present in arr, otherwise it will not be inserted and the current index will be returned.
 * @param comparator Comparator to sort array
 * @returns The index element in the array
 */
export function binaryInsert<T>(arr: T[], target: T, allowDuplicate?: boolean, comparator?: (a: T, b: T) => number): number {
    let i = binarySearch(arr, target, comparator);
    if (i >= 0) { /* if the binarySearch return value was zero or positive, a matching object was found */
        if (!allowDuplicate) {
            return i;// return position without inserting a duplicate
        }
    } else { /* if the return value was negative, the bitwise complement of the return value is the correct index for this object */
        i = ~i;// equivalent to -i -1
    }

    arr.splice(i, 0, target);
    return i;
}

/**
 * Gets compact string ex: getCompactString([2,3,4,5,8], [1,2,3,4,5,6,7,8,9], ",", "-") => 2-5,8
 * @template T The array type
 * @param params Function paramaters to compact array as a string
 * @param params.dataSorted the data array to compact as a string
 * @param params.fullDataSorted the 'full' data array that allows the function to identify successive values presence
 * @param params.singleSeparator (optional, defaut ',') the separator to use when element are not successive
 * @param params.rangeSeparator (optional, defaut '-') the separator to use when representing a range of elements
 * @param params.toString (optional, default (t) => String(t)) function that converts a T instance to a string
 * @returns the 'compact' string
 */
export function getCompactString<T>(params: {
    dataSorted: T[] | null,
    fullDataSorted: T[],
    singleSeparator?: string,
    rangeSeparator?: string,
    toString?: (t: T) => string
}): string {
    let str = "";
    if (params.dataSorted) {
        const singleSeparator = params.singleSeparator ?? ",";
        const rangeSeparator = params.rangeSeparator ?? "-";
        const currentToString = Object.prototype.hasOwnProperty.call(params, "toString") && params.toString ? params.toString : (t: T): string => String(t);
        let firstPresent: T | null = null;
        let lastPresent: T | null = null;

        for (const t of params.fullDataSorted) {
            const present: boolean = params.dataSorted.filter((e: T) => String(t) === String(e)).length ? true : false;
            if (present) {
                if (lastPresent == null) { firstPresent = t; }
                lastPresent = t;
            } else {
                if (firstPresent != null && lastPresent != null) {
                    const cur: string = lastPresent === firstPresent ? currentToString(firstPresent) : currentToString(firstPresent) + rangeSeparator + currentToString(lastPresent);
                    str = str === "" ? cur : str + singleSeparator + cur;
                }
                firstPresent = null;
                lastPresent = null;
            }
        }
        if (firstPresent != null && lastPresent != null) {
            const cur: string = lastPresent === firstPresent ? currentToString(firstPresent) : currentToString(firstPresent) + rangeSeparator + currentToString(lastPresent);
            str = str === "" ? cur : str + singleSeparator + cur;
        }
    }
    return str;
}

/**
 * Gets compact string integer ex : getCompactString([2,3,4,5,8], ",", "-") => 2-5,8
 * @param params Function paramaters to compact array as a string
 * @param params.dataSorted the sorted number array to compact as a string
 * @param params.singleSeparator (optional, defaut ',') the separator to use when element are not successive
 * @param params.rangeSeparator (optional, defaut '-') the separator to use when representing a range of elements
 * @param params.toString (optional, default (n) => String(n)) function that converts a T instance to a string
 * @returns  the 'compact' string
 */
export function getCompactStringInteger(params: {
    dataSorted: number[] | null,
    singleSeparator?: string,
    rangeSeparator?: string,
    toString?: (integer: number) => string
}): string {
    if (!Array.isArray(params.dataSorted)) {
        return "";
    }
    const singleSeparator = params.singleSeparator ?? ",";
    const rangeSeparator = params.rangeSeparator ?? "-";
    const currentToString = Object.prototype.hasOwnProperty.call(params, "toString") && params.toString ? params.toString : (e: number): string => String(e);
    let str = "";
    let firstPresent: number | null = null;
    let lastPresent: number | null = null;
    let previousValue: number | null = null;
    for (let i = 0; i < params.dataSorted.length + 1; i++) {
        const t = params.dataSorted[i];

        if (firstPresent == null) {
            firstPresent = t;
        } else if (t - 1 === previousValue) {
            lastPresent = t;
        } else if (previousValue != null && lastPresent == null) {
            if (str.length > 0) { str += singleSeparator; }
            str += currentToString(previousValue);
            firstPresent = t;
        } else if (lastPresent != null) {
            if (str.length > 0) { str += singleSeparator; }
            str += currentToString(firstPresent) + rangeSeparator + currentToString(lastPresent);
            lastPresent = null;
            firstPresent = t;
        }
        previousValue = t;
    }
    return str;
}

/** 
 * swap elements array[i] and array[j]
 * we use _"destructuring assignment"_ syntax to achieve that
 * same can be written as:
 * ```
 * let t = array[i]; array[i] = array[j]; array[j] = t
 * ```
 * @template T Array type
 * @param ar The array we shuffle randomly
 * @see https://bost.ocks.org/mike/shuffle/
 */
export function shuffleArray<T>(ar: T[]): void {
    if (!Array.isArray(ar) || !ar.length) { return; }

    let m = ar.length;
    let i: number;

    // While there remain elements to shuffle…
    while (m) {

        // Pick a remaining element…
        i = Math.floor(Math.random() * m--);

        // And swap it with the current element.
        [ar[m], ar[i]] = [ar[i], ar[m]];
    }

}

/**
 * Returns the first index at which a search element can be found in the array from a specific index
 * https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference/Global_Objects/Array/indexOf
 * @param array array where search element
 * @param searchElement search element
 * @param equal equality function
 * @param fromIndex starting index (if negative will start from array.length + fromIndex)
 * @returns index of the search element or -1 if it does not exists
 */
export function indexOf<T>(array: T[] | undefined, searchElement: T, equal?: (o1: T, o2: T) => boolean, fromIndex = 0): number {
    if (!Array.isArray(array)) { return -1; }
    if (!equal) {
        return array.indexOf(searchElement, fromIndex);
    }
    if (fromIndex < 0) {
        // if fromIndex is negative, we start from (length - offset)
        fromIndex = array.length + fromIndex;
    }
    const len = array.length;
    for (let k = Math.max(0, fromIndex); k < len; k++) {
        const currentElement = array[k];
        if (equal(searchElement, currentElement)) {
            return k;
        }
    }
    return -1;
}

/**
 * Remove first occurence of element in array [2,3,4,3,5,8] => [2,4,3,5,8]
 * @param array array where remove element
 * @param element element to remove
 * @param equal equality function
 * @returns true if element found
 */
export function removeFirstFromArray<T>(array: T[], element: T, equal?: (o1: T, o2: T) => boolean): boolean {
    const i = indexOf(array, element, equal);
    if (i !== -1) {
        array.splice(i, 1);
        return true;
    }
    return false;
}

/**
 * Remove all occurrences of element in array
 * 
 * @param array array where remove element
 * @param element element to remove
 * @param equal equality function
 * @returns true if element found
 */
export function removeAllOccurrencesFromArray<T>(array: T[], element: T, equal?: (o1: T, o2: T) => boolean): boolean {
    let removed = false;
    for (let i = 0; i < array.length; i++) {
        if (equal && equal(element, array[i]) || element === array[i]) {
            array.splice(i, 1);
            i--;
            removed = true;
        }
    }
    return removed;
}

/**
 * Remove all occurrences of all elements in array
 * 
 * @param array array where remove elements
 * @param elements elements to remove
 * @param equal equality function
 * @returns  true if one element found
 */
export function removeElementsFromArray<T>(array: T[], elements: T[], equal?: (o1: T, o2: T) => boolean): boolean {
    let removed = false;
    if (elements) {
        for (const element of elements) {
            for (let i = 0; i < array.length; i++) {
                if (equal && equal(element, array[i]) || element === array[i]) {
                    array.splice(i, 1);
                    i--;
                    removed = true;
                }
            }
        }
    }
    return removed;
}

/**
 * Return true if arr1 and arr2 contain at least one common element
 */
export function intersects<T>(arr1: T[] | null | undefined, arr2: T[] | null | undefined): boolean {
    if (arr1 == null || arr2 == null) { return false; }
    if (arr1.length > arr2.length) {
        return intersects(arr2, arr1);// minimize iteration count
    }
    const set2 = new Set<T>(arr2);
    for (const i of arr1) {
        if (set2.has(i)) { return true; }
    }
    return false;
}

/**
 * Return true if element is including in array of elements
 *
 * @template T Array type
 * @param elements Array of elements
 * @param searchElement Element to search in array
 * @param comparator Comparator function
 * @returns {*}  {boolean}
 */
export function includes<T>(elements: T[], searchElement: T, comparator?: (o1: T, o2: T) => boolean): boolean {
    return indexOf(elements, searchElement, comparator) !== -1;
}

/**
 * Push element in array if not already exists
 * ! don't use this function for large arrays, if you have a large array, use a set, a record or a map
 * @deprecated don't use this method in loop, very low perfs
 *
 * @template T Array type
 * @param ar Array to push element
 * @param elt Element to push
 * @param comparator Comparator function
 * @returns true if at element is added
 */
export function pushIfNew<T>(ar: T[], elt: T, comparator?: (o1: T, o2: T) => boolean): boolean {
    if (Array.isArray(ar) && !includes(ar, elt, comparator)) {
        ar.push(elt);
        return true;
    }
    return false;
}

/**
 * Push element in array if elt is not null or undefined and does not already exists in ar
 * ! don't use this function for large arrays, if you have a large array, use a set, a record or a map
 * @deprecated don't use this method in loop, very low perfs
 *
 * @template T Array type
 * @param ar Array to push element
 * @param elt Element to push
 * @param comparator Comparator function
 * @returns true if at element is added
 */
export function pushIfNewAndDefined<T>(ar: T[], elt: T | null | undefined, comparator?: (o1: T, o2: T) => boolean): boolean {
    if (Array.isArray(ar) && elt != null && !includes(ar, elt, comparator)) {
        ar.push(elt);
        return true;
    }
    return false;
}

/**
 * Push elements in array if not already existing
 * ! don't use this function for large arrays, if you have a large array, use a set, a record or a map
 * @deprecated don't use this method in loop, very low perfs
 *
 * @template T Array type
 * @param ar Array to push element
 * @param eltArray Elements to push
 * @param comparator Comparator function
 * @returns true if at least one element added
 */
export function pushAllIfNewAndDefined<T>(ar: T[], eltArray: T[] | null | undefined, comparator?: (o1: T, o2: T) => boolean): boolean {
    let result = false;
    if (Array.isArray(eltArray)) {
        for (const elt of eltArray) {
            if (elt != null && !includes(ar, elt, comparator)) {
                ar.push(elt);
                result = true;
            }
        }
    }
    return result;
}

/**
 * Returns the first element in the array where match(elt)
 *
 * @template T Array type
 * @param ar Array of elements
 * @param match Match function
 * @returns {*}  {(T | undefined)}
 */
export function findFirstMatch<T>(ar: T[], match: (elt: T) => boolean): T | undefined {
    if (!Array.isArray(ar)) {
        return undefined;
    }
    for (const elt of ar) {
        if (match(elt)) {
            return elt;
        }
    }
    return undefined;
}

/**
 * Returns the next element after a referring element in an array
 * @param array {T[]} The array to browse
 * @param ref_element {T} Referring element in array
 * @returns The next element after ref_element. Loop if ref_element is the last value of the array.
 */
export function nextElement<T>(array: T[], ref_element: T): T | undefined {
    if (!Array.isArray(array) || !array.length) {
        return undefined;
    }
    const index = array.indexOf(ref_element);
    if (index >= 0) {
        let newIndex = index + 1;
        if (newIndex >= array.length) {
            newIndex = 0;
        }
        return array[newIndex];
    }

    return undefined;
}

/**
 * Returns the previous element before a referring element in an array
 * @param array {T[]} The array to browse
 * @param ref_element {T} Referring element in array
 * @returns The previous element after ref_element. Loop if ref_element is the first value of the array.
 */
export function prevElement<T>(array: T[], ref_element: T): T | undefined {
    if (!Array.isArray(array) || !array.length) {
        return undefined;
    }
    const index = array.indexOf(ref_element);
    if (index >= 0) {
        let newIndex = index - 1;
        if (newIndex < 0) {
            newIndex = array.length - 1;
        }
        return array[newIndex];
    }

    return undefined;
}

export enum IteratorControl { CONTINUE = 1, BREAK }

/**
 * Iterate an array in a simple analytic way to put at disposition: the previous, current and next element in the iterator function.
 * 
 * Previous element will be undefined at the first call and next element at the last call of the function.
 * 
 * @param array data to iterate
 * @param iterator iterator with the previous, current et next element if the iterator returns IteratorControl to continue or break the iteration
 * @returns 
 */
export function analyticsIterator<DataType>(array: DataType[] | null | undefined, iterator: (previous: DataType | undefined, current: DataType, next: DataType | undefined) => IteratorControl | void): void {
    if (array == null) {
        return;
    }
    for (let index = 0; index < array.length; index++) {
        const control = iterator(array[index - 1], array[index], array[index + 1]);
        if (control === IteratorControl.BREAK) { break; }
    }
}

export interface BaseComparisonRes<T> {

    /** Elements added in newArray from oldArray */
    toAdd: T[];

    /** Elements removed in new Array from oldArray */
    toRemove: T[];

    /** Elements keep in newArray from oldArray */
    toKeep: T[];
}


export interface ComparisonRes<T> extends BaseComparisonRes<T> {

    /** Elements updated in newArray from oldArray */
    toUpdate: T[];
}

/** Compare two arrays of objects */
export function compareArrays<T>(params: {
    /** Old array to be compared */
    oldArray: T[],

    /** New array to compare with */
    newArray: T[],

    /** Equal matching function between two items */
    matchingElementsFct: (obj1: T, obj2: T) => boolean,

    /** Compare items, return true if old array should be updated with new obj */
    needsUpdateFct?: (obj1: T, obj2: T) => boolean,

    /** Return removable status of an item, if not given, all items are removable */
    canBeRemovedFct?: (obj: T) => boolean

}): ComparisonRes<T> {

    const res: ComparisonRes<T> = {
        toAdd: [],
        toUpdate: [],
        toRemove: [...params.oldArray],// Remove reference
        toKeep: []
    };

    // First pass: add/update elements
    for (const newArrElt of params.newArray) {
        let found = false;
        for (const oldArrElt of params.oldArray) {
            if (!params.matchingElementsFct(oldArrElt, newArrElt)) { continue; }

            found = true;

            // Remove from toRemove array because the element will be kept or updated
            for (let i = 0; i < res.toRemove.length; ++i) {
                const r = res.toRemove[i];

                if (params.matchingElementsFct(oldArrElt, r)) {
                    res.toRemove.splice(i, 1);
                    break;
                }
            }

            // If the two elements do not match perfectly, the old one should be updated
            if (params?.needsUpdateFct?.(oldArrElt, newArrElt)) {
                res.toUpdate.push(newArrElt);
            } else {
                res.toKeep.push(newArrElt);
            }

            break;
        }

        // If the new element was not already in the old array, it should be added
        if (!found) {
            res.toAdd.push(newArrElt);
        }
    }
    // Second pass: remove elements
    if (params.canBeRemovedFct) {
        for (let i = res.toRemove.length - 1; i >= 0; --i) {
            const r = res.toRemove[i];
            if (!params.canBeRemovedFct(r)) {
                res.toRemove.splice(i, 1);
            }
        }
    }

    return res;
}


/**
 * Check arrays equality, ignoring order of arrays
 * @see https://stackoverflow.com/questions/37065663/array-of-object-deep-comparison-with-lodash
 * @returns true if arrays contains same values
 */
export function arraysEqualIgnoreOrder<T>(arr1: T[] | undefined, arr2: T[] | undefined): boolean {

    if (!arr1 || !arr2 || arr1.length !== arr2.length) { return false; }
    if (arr1 === arr2) { return true; }

    // Check each items equality
    return isEmpty(xorWith(arr1, arr2, isEqual));
}

/**
 * @returns a new array without duplicates (and without nulls or empty strings depending on passed options), 
 * objects are not compared (only removes references to the same object, not to identical objects)
 * if getValue function is provided the values references are compared instead of the object references
 */
export function filterDuplicates<T>(arr: T[], options?: FilterEmptyNullOrUndefinedWithGetValue<T>): T[] {
    const resultSet = new Map<anyReallyAny, T>();
    if (arr) {
        for (const item of arr) {
            if (!checkFilterEmptyNullOrUndefined(item, options)) { continue; }

            const value = options?.getValue ? options.getValue(item) : item;
            resultSet.set(value, item);
        }
    }
    return Array.from(resultSet.values());
}

/**
 * @returns a new array without duplicates or undefined or null values (and without empty strings depending on passed options), 
 * objects are not compared (only removes references to the same object, not to identical objects)
 * if getValue function is provided the values references are compared instead of the object references
 */
export function filterDuplicatesAndNotDefined<T>(arr: (T | undefined)[], options?: FilterEmptyNullOrUndefinedWithGetValue<T | undefined>): T[] {
    return filterDuplicates<T | undefined>(arr, { ...options, filterNullOrUndefined: true }) as T[];
}

/**
 * @returns true if array has duplicates (and without nulls or empty strings depending on passed options), 
 * objects are not compared (only removes references to the same object, not to identical objects)
 * if getValue function is provided the values references are compared instead of the object references
 */
export function hasDuplicates<T>(
    arr: T[],
    options?: FilterEmptyNullOrUndefinedWithGetValue<T>
): boolean {
    const resultSet = new Map<anyReallyAny, T>();
    if (arr) {
        for (const item of arr) {
            if (!checkFilterEmptyNullOrUndefined(item, options)) { continue; }
            const value = options?.getValue ? options.getValue(item) : item;
            // Check if value is already checked
            if (resultSet.has(value)) { return true; }// return when first duplicate is found

            // Add value to list of values checked
            resultSet.set(value, item);
        }
    }

    return false;
}

/**
 * @returns list of duplicates elements in an array (and without nulls or empty strings depending on passed options), 
 * objects are not compared (only removes references to the same object, not to identical objects)
 * if getValue function is provided the values references are compared instead of the object references
 */
export function getDuplicates<T>(
    arr: T[],
    options?: FilterEmptyNullOrUndefinedWithGetValue<T>
): T[] {
    const resultSet = new Map<anyReallyAny, T>();
    const duplicatesSet = new Map<anyReallyAny, T>();
    if (arr) {
        for (const item of arr) {
            if (!checkFilterEmptyNullOrUndefined(item, options)) { continue; }

            const value = options?.getValue ? options.getValue(item) : item;
            // Check if value is already checked
            if (resultSet.has(value)) { duplicatesSet.set(value, item); }
            else {
                // Add value to list of values checked
                resultSet.set(value, item);
            }
        }
    }

    return Array.from(duplicatesSet.values());
}

export interface FilterEmptyNullOrUndefined {
    /** Filter null or undefined values */
    filterNullOrUndefined?: boolean;
    /** Filter empty string value */
    filterEmptyString?: boolean;

}
export interface FilterEmptyNullOrUndefinedWithGetValue<T> extends FilterEmptyNullOrUndefined {
    /** Override getValue, used it to compare objects */
    getValue?: (t: T) => anyReallyAny;
}

/**
 * @returns a new array without null, undefined items (and empty strings depending on options)
 */
export function filterNotDefined<T>(arr: (T | null | undefined)[], options?: {
    filterEmptyString?: boolean
}): T[] {
    if (!arr || !arr.length) { return []; }

    const resultArray = [];
    for (const item of arr) {
        if (!checkFilterEmptyNullOrUndefined(item, { filterEmptyString: options?.filterEmptyString, filterNullOrUndefined: true })) {
            continue;
        }

        resultArray.push(item);
    }
    return resultArray;
}

/** 
 * Check if item is null or undefined, or an empty string
 * @returns true if item isn't null or undefined or emptyString (depending on options)
 */
function checkFilterEmptyNullOrUndefined<T>(item: T, p?: FilterEmptyNullOrUndefined): item is NonNullable<T> {
    if (p?.filterNullOrUndefined && item == null) {
        return false;
    }
    if (p?.filterEmptyString && item as unknown === "") {
        return false;
    }
    return true;
}

/**
 * Wrap a value, list of values to array
 * Ex: (data: string|string[]) => wrapAsArray(data): string[]
 */
export function wrapAsArray<T>(value: T | T[] | null | undefined): T[] {
    if (value == null) { return []; }
    if (Array.isArray(value)) { return value; }
    return [value];
} 